// Cloudflare Turnstile tokens for Supabase Auth's bot protection: anonymous sign-in, email codes
// and native Sign in with Apple. Off until VITE_TURNSTILE_SITE_KEY is set at build time.
//
// Order matters: ship a build with the site key FIRST, then turn on CAPTCHA protection in Supabase.
// The other way round, sign-in fails for everyone until the new build is out.
//
// The widget is invisible unless Cloudflare needs the person to tick a box, in which case it appears
// at the bottom of the screen. Each token is single-use, so one is fetched per call.

import { config } from './config';

interface Turnstile {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  remove(id: string): void;
}
declare global { interface Window { turnstile?: Turnstile } }

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TIMEOUT_MS = 30_000;
let loading: Promise<Turnstile> | null = null;

export class CaptchaError extends Error {}

function load(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<Turnstile>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new CaptchaError('Verification didn’t load.')));
    s.onerror = () => { loading = null; reject(new CaptchaError('Verification didn’t load. Check your connection.')); };
    document.head.appendChild(s);
  });
  return loading;
}

function host(): HTMLElement {
  let el = document.getElementById('snapcook-captcha');
  if (!el) {
    el = document.createElement('div');
    el.id = 'snapcook-captcha';
    el.style.cssText = 'position:fixed;left:50%;transform:translateX(-50%);bottom:calc(var(--safe-bottom, 0px) + 84px);z-index:70';
    document.body.appendChild(el);
  }
  return el;
}

/** A fresh token when bot protection is on; undefined when it's off (no site key in this build). */
export async function captchaToken(siteKey = config.turnstileSiteKey): Promise<string | undefined> {
  if (!siteKey) return undefined;
  const ts = await load();
  return new Promise<string>((resolve, reject) => {
    let id = '';
    const done = (fn: () => void) => { clearTimeout(timer); try { if (id) ts.remove(id); } catch { /* already gone */ } fn(); };
    const timer = setTimeout(() => done(() => reject(new CaptchaError('Verification took too long. Please try again.'))), TIMEOUT_MS);
    id = ts.render(host(), {
      sitekey: siteKey,
      appearance: 'interaction-only',
      callback: (token: string) => done(() => resolve(token)),
      'error-callback': () => done(() => reject(new CaptchaError('Verification failed. Please try again.'))),
      'expired-callback': () => done(() => reject(new CaptchaError('Verification expired. Please try again.'))),
    });
  });
}
